import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { toast } from 'material-react-toastify';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { useThemeColor } from '../../hooks/useThemeColor';
import { isTokenValid } from '../../utils/Auth';
import { validatePassword } from '../../utils/Validation';
import { resetPassword } from '../../api/Auth';
import { bodyWidth } from '../../utils/Style';
import { GenericHelmet } from '../../components/Helmet/GenericHelmet';
import { useLocalizedText } from '../../hooks/useLocalizedText';

export const ResetPasswordPage = () => {
  const t = useLocalizedText();

  const navigate = useNavigate();
  const { email, code } = useParams<string>();

  const { primary, secondary } = useThemeColor();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isTokenValid()) {
      navigate('/account');
    }
  }, []);

  const handleSubmit = () => {
    if (!email || !code) {
      toast.error(t('reset_password.invalid'));
      return;
    }

    setLoading(true);
    resetPassword({ email, code, password })
      .then(() => {
        toast.success(t('reset_password.success'));
        navigate('/giris-yap');
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  const isPasswordValid = validatePassword(password);
  const isConfirmPasswordValid = password === confirmPassword;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        alignItems: 'center',
      }}
    >
      <GenericHelmet title={t('reset_password.set_new_password')} />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: bodyWidth,
          py: 3,
          px: 1.5,
          gap: 1.5,
          boxSizing: 'border-box',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            px: 2.5,
            py: 2,
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            width: '100%',
          }}
        >
          <Typography sx={{ fontSize: '1.75rem', fontWeight: 600 }}>
            {t('reset_password.set_new_password')}
          </Typography>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              width: '100%',
            }}
          >
            <Typography>{t('common.email')}</Typography>

            <TextField
              size='small'
              fullWidth
              disabled
              value={email}
              sx={{ maxWidth: 400 }}
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              width: '100%',
            }}
          >
            <Typography>{t('common.password')}</Typography>
            <TextField
              size='small'
              fullWidth
              disabled={loading}
              type='password'
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={!!password.length && !isPasswordValid}
              helperText={
                isPasswordValid || !password.length
                  ? undefined
                  : t('validation.password_length')
              }
              sx={{ maxWidth: 400 }}
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              width: '100%',
            }}
          >
            <Typography>{t('common.confirm_password')}</Typography>
            <TextField
              size='small'
              fullWidth
              disabled={loading}
              type='password'
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              error={!!password.length && !isConfirmPasswordValid}
              helperText={
                isConfirmPasswordValid || !password.length
                  ? undefined
                  : t('validation.passwords_does_not_match')
              }
              sx={{ maxWidth: 400 }}
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 1,
              alignItems: 'center',
            }}
          >
            <Button
              variant='contained'
              onClick={handleSubmit}
              disabled={loading || !isPasswordValid || !isConfirmPasswordValid}
              sx={{
                textTransform: 'none',
                backgroundColor: primary,
                color: secondary,
              }}
            >
              {t('common.confirm')}
            </Button>

            {loading && <CircularProgress color='primary' />}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};
